<template>
  <OperationFormLayoutSkeleton v-if="loading" />
  <OperationFormLayout
    v-if="!loading && currentOrganisation"
    :id="stationId"
    :formDisabled="!imageValid"
    :formSaving="saving"
    operationType="station"
    @cancel="$router.replace({ name: 'shaftV2ConfigurationStations' })"
    @update="save"
  >
    <OperationFormSection
      :modifiers="['border-radius-top']"
      :sectionDescription="$t('shafts.group.stations.details_description')"
      :sectionTitle="$t('shafts.group.stations.details_title')"
    >
      <FormRow
        :errors="errors?.station?.name"
        :label="$t('shafts.group.stations.name_label')"
        labelFor="station_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': errors?.station?.name }">
            <input
              id="station_name"
              v-model="station.name"
              :placeholder="$t('shafts.group.stations.input_name')"
              @input="validateInput('station', 'name')"
            >
          </div>
        </div>
      </FormRow>

      <div
        class="
          form-layout__row--mobile
          form-layout__row--inline
          form-layout__row--margin-top
        "
      >
        <div class="form-layout__column form-layout__column--inline-input">
          <FormRow
            :errors="errors?.station?.start_set"
            :label="$t('shafts.group.stations.start_set')"
            labelFor="station_start_set"
            :modifiers="['no-padding']"
          >
            <div class="form-row__input form-row__input--no-peek">
              <div
                :class="{
                  input: true,
                  'input--error': errors?.station?.start_set,
                }"
              >
                <input
                  id="station_start_set"
                  v-model="station.start_set"
                  :placeholder="$t('shafts.group.stations.input_set')"
                  @blur="!startSetWasTouched && validateInput('start_set', 'number')"
                >
              </div>
            </div>
          </FormRow>
        </div>

        <div class="form-layout__column form-layout__column--inline-input">
          <FormRow
            :errors="errors?.station?.end_set"
            :label="$t('shafts.group.stations.end_set')"
            labelFor="station_end_set"
            :modifiers="['no-padding']"
          >
            <div class="form-row__input form-row__input--no-peek">
              <div
                :class="{
                  input: true,
                  'input--error': errors?.station?.end_set,
                }"
              >
                <input
                  id="station_end_set"
                  v-model="station.end_set"
                  :placeholder="$t('shafts.group.stations.input_set')"
                  @blur="!endSetWasTouched && validateInput('end_set', 'number')"
                >
              </div>
            </div>
          </FormRow>
        </div>
      </div>
    </OperationFormSection>

    <!-- Diagram section -->
    <NoticeMessageContainer v-if="station?.diagram_thumbnail_url && warningText">
      <NoticeMessage
        colorStyle="warning"
        iconStyle="warning"
        :message="warningText"
        :modifiers="['narrow']"
      />
    </NoticeMessageContainer>
    <OperationFormSection
      :modifiers="['border-radius-bottom']"
      :sectionDescription="$t('shafts.group.stations.diagram_description')"
      :sectionTitle="$t('shafts.group.stations.diagram_title')"
    >
      <FormRow :modifiers="['no-padding']">
        <template v-if="diagramImage">
          <img
            class="diagram-image"
            :src="diagramImage"
          >
          <FormStandAloneErrorMessage
            v-if="!imageValid"
            :errorMessage="imageValidationMessage"
          />
          <div
            :class="{
              'form-layout__row--mobile form-layout__row--inline': stationId,
              'form-layout__row--margin-top': true,
            }"
          >
            <div
              :class="{
                'form-layout__column form-layout__column--inline-button':
                  stationId,
              }"
            >
              <label
                class="diagram-button button"
                for="station_change_diagram"
              >
                {{ $t('shafts.group.stations.change_diagram') }}
                <FileUploader
                  id="station_change_diagram"
                  :acceptedFileTypes="acceptedFileTypes"
                  fileType="image"
                  :label="$t('issues.add_image')"
                  :modifiers="['hide']"
                  :multiple="false"
                  @update-blob="updateBlob"
                  @update-error="updateError"
                  @update-reader="updateReader"
                />
              </label>
            </div>
            <div
              v-if="stationId"
              class="form-layout__column form-layout__column--inline-button"
            >
              <router-link
                class="button"
                :to="{ name: 'shaftV2ManageRegions' }"
              >
                <ManageRegionSvg class="button__icon button--margin-right" />
                {{ $t('shafts.group.stations.manage_regions') }}
              </router-link>
            </div>
          </div>
        </template>
        <label
          v-else
          class="diagram-button button"
          for="station_diagram"
        >
          {{ $t('shafts.group.stations.upload_diagram') }}
          <FileUploader
            id="station_diagram"
            :acceptedFileTypes="acceptedFileTypes"
            fileType="image"
            :label="$t('issues.add_image')"
            :modifiers="['hide']"
            :multiple="false"
            @update-blob="updateBlob"
            @update-error="updateError"
            @update-reader="updateReader"
          />
        </label>
        <FormStandAloneErrorMessage
          v-if="!imageValid && !diagramImage"
          :errorMessage="imageValidationMessage"
        />
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>

  <teleport
    v-if="boundsEditorOpen"
    to="body"
  >
    <BoundsEditor
      v-model="station.bounds"
      :overlays="station.map_tile_overlays"
      @close="boundsEditorOpen = false"
    />
  </teleport>
</template>

<script setup>
import { inject, onMounted, reactive, ref, watch } from 'vue';
import I18n from 'i18n-js';

// Utils
import { acceptedFileTypes } from '@/config/accepted_file_types';
import { useShaftV2 } from '@/functions/use_remote_object';
import { useRouter } from 'vue-router';
import useAjax from '@/functions/use_ajax';
import useUploader from '@/vendor/use_uploader';

// Components
import BoundsEditor from '@/components/operations/bounds_editor.vue';
import FormRow from '@/components/form/row.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';
import FileUploader from '@/components/generic/file_uploader.vue';
import NoticeMessageContainer from '@/components/generic/notice_message_container.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';

// Svg
import ManageRegionSvg from '@/images/manage-region.svg';

const createNewStation = () => ({
  name: '',
  symbol: '',
  start_set: null,
  end_set: null,
  map_tile_overlays: [],
});

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, default: null },
  stationId: { type: String, default: null },
});

const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();
const currentOrganisation = inject('currentOrganisation');
const currentShaft = useShaftV2(props);
const loading = ref(false);
const initialStation = ref(null);
const station = reactive(createNewStation());
const boundsEditorOpen = ref(false);
const errors = ref(null);
const saving = ref(false);
const diagramImage = ref(null);
const startSetWasTouched = ref(false);
const endSetWasTouched = ref(false);
let diagramUpload;

const imageValid = ref(true);
const imageValidationMessage = ref(null);
const warningText = ref(null);

// All endpoints that this component uses
const ENDPOINTS = {
  stations: `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/stations`,
  station: `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/stations/${props.stationId}`,
  get post_or_patch() {
    return props.stationId ? this.station : this.stations;
  },
};

const loadStation = async () => {
  if (props.stationId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.station);

    if (ok) {
      initialStation.value = data;
      Object.assign(station, data);
      diagramImage.value = data?.diagram_thumbnail_url;
      loading.value = false;
    }
  } else {
    initialStation.value = createNewStation();
  }
};

onMounted(() => {
  loadStation();
});

const formatStationForPost = async () => {
  let stationParams = {
    name: station.name,
    symbol: station.symbol,
    start_set: station.start_set,
    end_set: station.end_set,
  };

  if (diagramUpload) {
    let blob = await uploader.upload(diagramUpload);
    stationParams.diagram = {
      source: blob.signed_id,
      source_filename: blob.filename,
    };
  }

  return { station: stationParams };
};

const save = async () => {
  const testStartSet = errors.value?.station?.start_set;
  const testEndSet = errors.value?.station?.end_set;
  if (testStartSet || testEndSet) return;

  errors.value = null;
  saving.value = true;

  let method = props.stationId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: await formatStationForPost(),
  });

  if (ok) {
    router.replace({ name: 'shaftV2ConfigurationStations' });
  } else {
    errors.value = data;
  }

  saving.value = false;
};

function updateBlob(file) {
  if (!file?.upload) return;
  diagramUpload = file.upload;
}

function updateReader(file) {
  if (!file?.url) return;
  diagramImage.value = file.url;
  warningText.value = I18n.t('general.warning_text');
}

function updateError(file) {
  if (!file?.filename) return;

  if (!acceptedFileTypes.includes(file.filename)) {
    imageValidationMessage.value = I18n.t('general.image_validation_message', {
      filename: file.filename,
    });
    imageValid.value = false;
  } else {
    imageValid.value = true;
  }
}

/**
 * Listens to the start_set and end_set fields for changes,
 * runs the validations and sets errors accordingly
 */
watch(
  () => station.start_set,
  (value) => {
    startSetWasTouched.value = true;
    const testBlankAndNan = validateInputSet('start_set', 'number');
    const shaftNumberOfSets = parseFloat(currentShaft.value?.number_of_sets);

    !testBlankAndNan && validateSet(value, station.end_set, 'start_set');
    !testBlankAndNan &&
      validateSet(station.end_set, station.start_set, 'end_set');
    !testBlankAndNan && rangeValidation(value, shaftNumberOfSets, 'start_set');
  }
);

watch(
  () => station.end_set,
  (value) => {
    endSetWasTouched.value = true;
    const testBlankAndNan = validateInputSet('end_set', 'number');
    const shaftNumberOfSets = parseFloat(currentShaft.value?.number_of_sets);

    !testBlankAndNan && validateSet(value, station.start_set, 'end_set');
    !testBlankAndNan &&
      validateSet(station.start_set, station.end_set, 'start_set');
    !testBlankAndNan && rangeValidation(value, shaftNumberOfSets, 'end_set');
  }
);

function validateSet(value, limit, key) {
  if (isNaN(station?.start_set) || isNaN(station?.end_set)) return;
  if (!station?.start_set || !station?.end_set) return;

  const newValue = parseFloat(value);
  const startSetTest =
    !!newValue && key === 'start_set' ? newValue <= limit : newValue >= limit;

  const shaftNumberOfSets = parseFloat(currentShaft.value?.number_of_sets);
  const startSet = parseFloat(station?.start_set);
  const endSet = parseFloat(station?.end_set);

  const setTest =
    startSet === 0 ||
    endSet === 0 ||
    startSet > shaftNumberOfSets ||
    endSet > shaftNumberOfSets;

  if (!!newValue && startSetTest && !setTest) return clearValidationMessage(key);
  const label = key === 'start_set' ? 'max_limit' : 'min_limit';
  const zeroMessage = [
    I18n.t('activerecord.errors.between_set', { type: shaftNumberOfSets }),
  ];
  const defaultMessage = [
    I18n.t(`activerecord.errors.${label}`, { type: !limit ? 1 : limit }),
  ];
  const errorMessage = setTest ? zeroMessage : defaultMessage;

  if (!setTest) return setValidationMessage(errorMessage, key);
  setValidationMessage(errorMessage, 'start_set');
  setValidationMessage(errorMessage, 'end_set');
}

function validateInput(type, field) {
  if (errors.value && errors.value[type]) {
    errors.value[type][field] = null;
  }
}

function validateInputSet(key, type) {
  const value = station[key] || station[key];
  let errorMessage;

  if (type === 'number') {
    errorMessage = [I18n.t('activerecord.errors.NAN')];
    if (isNaN(value)) return ((errors.value = {}) && setValidationMessage(errorMessage, key));

    errorMessage = [I18n.t('activerecord.errors.min_limit', { type: 1 })];
    if (value === 0) return setValidationMessage(errorMessage, key);
  }

  clearValidationMessage(key);
}

function rangeValidation(value, limit, key) {
  if (isNaN(limit) || (value <= limit && value <= Number(currentShaft.value.number_of_sets) && value > 0)) return;
  const message = [I18n.t('activerecord.errors.between_set', { type: limit })];
  return setValidationMessage(message, key);
}

function setValidationMessage(message, key) {
  if (errors?.value?.station)
    return (errors.value.station[key] = message);
  return (errors.value = {
    station: {
      [key]: message,
    },
  });
}

function clearValidationMessage(key) {
  !!errors?.value?.station && (errors.value.station[key] = null);
}
</script>
